/**
 * BillingDropdown - Web-only dropdown for billing documents
 *
 * Displays PDF invoices/receipts from HubSpot engagement attachments.
 * Anchored below the FleetHub header, right-aligned.
 * Matches FleetHub design language: glass-morphism, Apple-style layered
 * shadows, hairline separators, and card-based document rows.
 *
 * @module components/BillingDropdown
 */

import { FC, useCallback, useEffect, useRef, useState } from "react"
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  TextStyle,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native"
import * as Sentry from "@sentry/react"
import {
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  ClipboardCheck,
  Download,
  FileText,
  RefreshCw,
  X,
} from "lucide-react"

import { Text } from "@/components/Text"
import {
  type BillingDocument,
  type InspectionReport,
  getCompanyBillingDocuments,
  getReportDownloadUrl,
} from "@/services/firebase/functions"
import { breakpoints, colors, spacing } from "@/theme"
import { openTrustedUrl } from "@/utils/openTrustedUrl"

const DOCS_PER_PAGE = 15

interface BillingDropdownProps {
  visible: boolean
  onClose: () => void
}

/**
 * Format file size in human-readable form (KB/MB)
 */
function formatFileSize(bytes?: number): string {
  if (!bytes) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Format ISO date string to German locale (DD.MM.YYYY)
 */
function formatDate(isoDate: string): string {
  const [year, month, day] = isoDate.split("-")
  return `${day}.${month}.${year}`
}

export const BillingDropdown: FC<BillingDropdownProps> = ({ visible, onClose }) => {
  const [documents, setDocuments] = useState<BillingDocument[]>([])
  const [reports, setReports] = useState<InspectionReport[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasFetched, setHasFetched] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [reportsPage, setReportsPage] = useState(1)
  const { width } = useWindowDimensions()
  const isMobile = width < breakpoints.mobile

  // Invoice pagination
  const totalPages = Math.ceil(documents.length / DOCS_PER_PAGE)
  const paginatedDocs = documents.slice(
    (currentPage - 1) * DOCS_PER_PAGE,
    currentPage * DOCS_PER_PAGE,
  )

  // Report pagination
  const totalReportPages = Math.ceil(reports.length / DOCS_PER_PAGE)
  const paginatedReports = reports.slice(
    (reportsPage - 1) * DOCS_PER_PAGE,
    reportsPage * DOCS_PER_PAGE,
  )

  // Inject CSS keyframes for animations (same pattern as Modal.web.tsx)
  useEffect(() => {
    if (typeof document === "undefined") return
    if (document.querySelector("#billing-dropdown-animations")) return

    const style = document.createElement("style")
    style.id = "billing-dropdown-animations"
    style.textContent = `
      @keyframes billingDropdownIn {
        from {
          opacity: 0;
          transform: translateY(-8px) scale(0.98);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `
    document.head.appendChild(style)
  }, [])

  // Track if we have cached data (don't refetch on re-open)
  const cachedRef = useRef(false)

  const fetchDocuments = useCallback(async (force = false) => {
    if (cachedRef.current && !force) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await getCompanyBillingDocuments()
      setDocuments(response.documents)
      setReports(response.reports || [])
      setCurrentPage(1)
      setReportsPage(1)
      cachedRef.current = true
      setHasFetched(true)
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden der Rechnungen.")
      setHasFetched(true)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Fetch on first open
  useEffect(() => {
    if (visible && !hasFetched && !isLoading) {
      fetchDocuments()
    }
  }, [visible, hasFetched, isLoading, fetchDocuments])

  // Close on Escape key
  useEffect(() => {
    if (!visible) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [visible, onClose])

  // Track which report is currently loading its download URL
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null)

  if (!visible) return null

  const handleDownload = (url: string) => {
    // Signed HubSpot URL from the backend: https-only + noopener (PLA-514).
    // A non-https URL coming from our own backend is a defect, not an
    // expected rejection (ADR-0036 tier 3).
    if (!openTrustedUrl(url)) {
      Sentry.captureException(new Error("Billing document download refused: untrusted URL"), {
        tags: { surface: "fleethub", operation: "billing_document_download" },
      })
    }
  }

  const handleReportDownload = async (report: InspectionReport) => {
    setDownloadingReportId(report.orderId)
    try {
      const result = await getReportDownloadUrl(report.orderId)
      if (!openTrustedUrl(result.url)) {
        const err = new Error("Ungültiger Download-Link")
        Sentry.captureException(err, {
          tags: { surface: "fleethub", operation: "report_download" },
        })
        throw err
      }
    } catch (err: any) {
      // Show a brief error — could be improved with a toast later
      console.error("Report download failed:", err.message)
    } finally {
      setDownloadingReportId(null)
    }
  }

  return (
    <>
      {/* Backdrop - closes dropdown on outside click */}
      <Pressable style={$backdrop} onPress={onClose} accessibilityRole="button" />

      {/* Dropdown panel */}
      <View style={[$dropdown, isMobile && $dropdownMobile]}>
        {/* Header — matches FleetHub header hairline border pattern */}
        <View style={$dropdownHeader}>
          <View style={$headerTitleRow}>
            <FileText size={18} color={colors.text} strokeWidth={2} />
            <Text style={$dropdownTitle}>Rechnungen</Text>
            {!isLoading && documents.length > 0 && (
              <View style={$countBadge}>
                <Text style={$countBadgeText}>{documents.length}</Text>
              </View>
            )}
          </View>
          <View style={$headerActions}>
            <Pressable
              onPress={() => fetchDocuments(true)}
              accessibilityRole="button"
              accessibilityLabel="Aktualisieren"
              style={$actionButton}
            >
              <RefreshCw
                size={16}
                color={colors.textDim}
                strokeWidth={2}
                // @ts-ignore - web CSS animation for loading spinner
                style={isLoading ? { animation: "spin 1s linear infinite" } : undefined}
              />
            </Pressable>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Schließen"
              style={$actionButton}
            >
              <X size={18} color={colors.textDim} strokeWidth={2} />
            </Pressable>
          </View>
        </View>

        {/* Info hint */}
        <View style={$infoBanner}>
          <CircleHelp
            size={14}
            color={colors.palette.secondary500}
            strokeWidth={2}
            style={{ flexShrink: 0, marginTop: 1 } as any}
          />
          <Text style={$infoBannerText}>
            Je nach Absprache mit dem Prüfer kann die Rechnung bereits auf dem Prüfbericht enthalten
            sein. In diesem Fall gibt es keine separate Rechnung — bitte im Prüfbericht nachsehen.
          </Text>
        </View>

        {/* Content */}
        <ScrollView
          style={[$scrollContent, !isMobile && $scrollContentDesktop]}
          showsVerticalScrollIndicator={false}
        >
          {/* Loading state */}
          {isLoading && (
            <View style={$stateContainer}>
              <ActivityIndicator size="small" color={colors.primary} />
              <Text style={$stateText}>Rechnungen werden geladen...</Text>
            </View>
          )}

          {/* Error state */}
          {!isLoading && error && (
            <View style={$stateContainer}>
              <Text style={$errorText}>{error}</Text>
              <Pressable
                onPress={() => fetchDocuments(true)}
                style={$retryButton}
                accessibilityRole="button"
              >
                <Text style={$retryButtonText}>Erneut versuchen</Text>
              </Pressable>
            </View>
          )}

          {/* Empty state — only when both invoices and reports are empty */}
          {!isLoading && !error && hasFetched && documents.length === 0 && reports.length === 0 && (
            <View style={$stateContainer}>
              <View style={$emptyIconContainer}>
                <FileText size={28} color={colors.palette.neutral400} strokeWidth={1.5} />
              </View>
              <Text style={$stateText}>Keine Rechnungen oder Prüfberichte vorhanden</Text>
            </View>
          )}

          {/* Document list — paginated */}
          {!isLoading &&
            !error &&
            paginatedDocs.map((doc, index) => (
              <Pressable
                key={doc.fileId}
                onPress={() => handleDownload(doc.downloadUrl)}
                accessibilityRole="link"
                accessibilityLabel={`${doc.filename} herunterladen`}
                // @ts-ignore - React Native Web hover callback
                style={({ hovered }: { hovered: boolean }) => [
                  $documentRow,
                  index < paginatedDocs.length - 1 && $documentRowBorder,
                  hovered && $documentRowHover,
                ]}
              >
                {/* PDF icon — visual anchor matching FleetOrderRow's left section */}
                <View style={$pdfIconContainer}>
                  <FileText size={18} color={colors.primary} strokeWidth={1.8} />
                </View>

                {/* File info */}
                <View style={$documentInfo}>
                  <Text style={$documentName} numberOfLines={1} ellipsizeMode="tail">
                    {doc.filename}
                  </Text>
                  <View style={$documentMeta}>
                    <Text style={$documentDate}>{formatDate(doc.date)}</Text>
                    {doc.size ? (
                      <>
                        <View style={$metaDot} />
                        <Text style={$documentSize}>{formatFileSize(doc.size)}</Text>
                      </>
                    ) : null}
                  </View>
                </View>

                {/* Download button — pill style matching header buttons */}
                <View style={$downloadButton}>
                  <Download size={16} color={colors.palette.neutral100} strokeWidth={2.2} />
                </View>
              </Pressable>
            ))}
        </ScrollView>

        {/* Invoice pagination — compact arrows */}
        {!isLoading && !error && totalPages > 1 && (
          <View style={$compactPagination}>
            <Pressable
              onPress={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              style={[$pageArrow, currentPage <= 1 && $pageArrowDisabled]}
              accessibilityLabel="Vorherige Seite"
            >
              <ChevronLeft
                size={16}
                color={currentPage <= 1 ? colors.palette.neutral400 : colors.palette.neutral100}
                strokeWidth={2.5}
              />
            </Pressable>
            <Text style={$pageLabel}>
              {currentPage} / {totalPages}
            </Text>
            <Pressable
              onPress={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              style={[$pageArrow, currentPage >= totalPages && $pageArrowDisabled]}
              accessibilityLabel="Nächste Seite"
            >
              <ChevronRight
                size={16}
                color={
                  currentPage >= totalPages ? colors.palette.neutral400 : colors.palette.neutral100
                }
                strokeWidth={2.5}
              />
            </Pressable>
          </View>
        )}

        {/* ─── Prüfberichte Section ─── */}
        {!isLoading && !error && hasFetched && reports.length > 0 && (
          <>
            {/* Section header */}
            <View style={$sectionHeader}>
              <View style={$headerTitleRow}>
                <ClipboardCheck size={16} color={colors.text} strokeWidth={2} />
                <Text style={$sectionTitle}>Prüfberichte</Text>
                <View style={$countBadge}>
                  <Text style={$countBadgeText}>{reports.length}</Text>
                </View>
              </View>
            </View>

            {/* Report rows */}
            <ScrollView
              style={[$scrollContent, !isMobile && $scrollContentDesktop]}
              showsVerticalScrollIndicator={false}
            >
              {paginatedReports.map((report, index) => {
                const isDownloading = downloadingReportId === report.orderId
                return (
                  <Pressable
                    key={`report-${report.orderId}`}
                    onPress={() => !isDownloading && handleReportDownload(report)}
                    disabled={isDownloading}
                    accessibilityRole="link"
                    accessibilityLabel={`Prüfbericht ${report.numberPlate} herunterladen`}
                    // @ts-ignore - React Native Web hover callback
                    style={({ hovered }: { hovered: boolean }) => [
                      $documentRow,
                      index < paginatedReports.length - 1 && $documentRowBorder,
                      hovered && !isDownloading && $documentRowHover,
                    ]}
                  >
                    {/* Report icon — green tint to distinguish from invoices */}
                    <View style={$reportIconContainer}>
                      <ClipboardCheck size={18} color={colors.success} strokeWidth={1.8} />
                    </View>

                    {/* Report info */}
                    <View style={$documentInfo}>
                      <Text style={$documentName} numberOfLines={1} ellipsizeMode="tail">
                        {report.filename}
                      </Text>
                      <View style={$documentMeta}>
                        <Text style={$documentDate}>{formatDate(report.date)}</Text>
                        {report.numberPlate ? (
                          <>
                            <View style={$metaDot} />
                            <Text style={$documentSize}>{report.numberPlate}</Text>
                          </>
                        ) : null}
                        {report.size ? (
                          <>
                            <View style={$metaDot} />
                            <Text style={$documentSize}>{formatFileSize(report.size)}</Text>
                          </>
                        ) : null}
                      </View>
                    </View>

                    {/* Download button — shows spinner while fetching signed URL */}
                    <View style={$downloadButton}>
                      {isDownloading ? (
                        <ActivityIndicator size={14} color={colors.palette.neutral100} />
                      ) : (
                        <Download size={16} color={colors.palette.neutral100} strokeWidth={2.2} />
                      )}
                    </View>
                  </Pressable>
                )
              })}
            </ScrollView>

            {/* Report pagination — compact arrows */}
            {totalReportPages > 1 && (
              <View style={$compactPagination}>
                <Pressable
                  onPress={() => setReportsPage((p) => Math.max(1, p - 1))}
                  disabled={reportsPage <= 1}
                  style={[$pageArrow, reportsPage <= 1 && $pageArrowDisabled]}
                  accessibilityLabel="Vorherige Seite"
                >
                  <ChevronLeft
                    size={16}
                    color={reportsPage <= 1 ? colors.palette.neutral400 : colors.palette.neutral100}
                    strokeWidth={2.5}
                  />
                </Pressable>
                <Text style={$pageLabel}>
                  {reportsPage} / {totalReportPages}
                </Text>
                <Pressable
                  onPress={() => setReportsPage((p) => Math.min(totalReportPages, p + 1))}
                  disabled={reportsPage >= totalReportPages}
                  style={[$pageArrow, reportsPage >= totalReportPages && $pageArrowDisabled]}
                  accessibilityLabel="Nächste Seite"
                >
                  <ChevronRight
                    size={16}
                    color={
                      reportsPage >= totalReportPages
                        ? colors.palette.neutral400
                        : colors.palette.neutral100
                    }
                    strokeWidth={2.5}
                  />
                </Pressable>
              </View>
            )}
          </>
        )}
      </View>
    </>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const $backdrop: ViewStyle = {
  position: "fixed" as any,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1001,
}

// Matches FleetHub's glass-morphism + FleetOrderRow's layered shadow system
const $dropdown: ViewStyle = {
  position: "fixed" as any,
  top: 64,
  right: 24,
  width: 400,
  maxHeight: "calc(100vh - 88px)" as any, // 64px top offset + 24px bottom gutter
  backgroundColor: "rgba(255, 255, 255, 0.96)",
  borderRadius: 14, // Matches FleetOrderRow $cardOuter
  borderWidth: 1,
  borderColor: "rgba(17, 30, 44, 0.04)", // Matches FleetOrderRow
  // @ts-ignore
  backdropFilter: "blur(20px) saturate(1.2)",
  // @ts-ignore
  WebkitBackdropFilter: "blur(20px) saturate(1.2)",
  // @ts-ignore - Apple-style layered shadow matching FleetOrderRow + modal
  boxShadow:
    "0 1px 2px rgba(17, 30, 44, 0.04), 0 4px 12px rgba(17, 30, 44, 0.08), 0 12px 32px rgba(17, 30, 44, 0.12)",
  // @ts-ignore
  animation: "billingDropdownIn 180ms ease-out",
  zIndex: 1002,
  overflow: "hidden",
}

const $dropdownMobile: ViewStyle = {
  left: 8,
  right: 8,
  width: "auto" as any,
  maxHeight: "70%" as any,
}

// Matches FleetHub header's hairline separator pattern
const $dropdownHeader: ViewStyle = {
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  paddingHorizontal: spacing.md, // 16px
  paddingVertical: spacing.sm, // 12px
  borderBottomWidth: 1,
  borderBottomColor: "rgba(17, 30, 44, 0.06)", // Same as header boxShadow border
}

const $headerTitleRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xs, // 8px
}

const $dropdownTitle: TextStyle = {
  fontSize: 16,
  fontWeight: "700",
  color: colors.text, // #111E2C
}

// Matches $headerSubtitle badge style from FleetHubScreen
const $countBadge: ViewStyle = {
  backgroundColor: colors.surfaceSecondary,
  borderRadius: 10,
  paddingHorizontal: 8,
  paddingVertical: 2,
  minWidth: 24,
  alignItems: "center",
  borderWidth: 1,
  borderColor: "rgba(17, 30, 44, 0.06)",
}

const $countBadgeText: TextStyle = {
  fontSize: 12,
  fontWeight: "600",
  color: colors.textDim, // #666666
}

const $headerActions: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 4,
}

const $actionButton: ViewStyle = {
  padding: 6,
  borderRadius: 8,
  // @ts-ignore
  cursor: "pointer",
  // @ts-ignore
  transition: "background-color 100ms ease",
}

// Info banner — subtle blue-tinted hint matching FleetHub's informational style
const $infoBanner: ViewStyle = {
  flexDirection: "row",
  alignItems: "flex-start",
  gap: 8,
  paddingHorizontal: 14,
  paddingVertical: 10,
  backgroundColor: "rgba(33, 150, 243, 0.04)", // secondary500 at 4%
  borderBottomWidth: 1,
  borderBottomColor: "rgba(17, 30, 44, 0.06)",
}

const $infoBannerText: TextStyle = {
  fontSize: 12,
  lineHeight: 17,
  color: colors.textDim,
  flex: 1,
}

const $scrollContent: ViewStyle = {
  maxHeight: 410,
}

// The panel height is the only limit on desktop; both lists share it via flex shrink
const $scrollContentDesktop: ViewStyle = {
  maxHeight: "none" as any,
}

const $stateContainer: ViewStyle = {
  alignItems: "center",
  justifyContent: "center",
  paddingVertical: spacing.xxl, // 48px
  paddingHorizontal: spacing.md,
  gap: spacing.sm, // 12px
}

const $emptyIconContainer: ViewStyle = {
  width: 56,
  height: 56,
  borderRadius: 28,
  backgroundColor: colors.palette.neutral200, // #F4F4F4
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 4,
}

const $stateText: TextStyle = {
  fontSize: 14,
  color: colors.textDim,
  textAlign: "center",
}

const $errorText: TextStyle = {
  fontSize: 14,
  color: colors.error,
  textAlign: "center",
}

const $retryButton: ViewStyle = {
  marginTop: spacing.xs,
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.xs,
  backgroundColor: colors.palette.neutral800, // Dark, matching header buttons
  borderRadius: 10, // Matches header button borderRadius
}

const $retryButtonText: TextStyle = {
  fontSize: 13,
  fontWeight: "600",
  color: colors.palette.neutral100, // White, matching header button text
}

// Document row — matches FleetOrderRow's padding/spacing rhythm
const $documentRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  paddingHorizontal: 14, // Matches $cardInner paddingLeft/Right
  paddingVertical: 12, // Matches $cardInner paddingTop/Bottom
  gap: 10, // Matches $contentContainer gap
  // @ts-ignore
  cursor: "pointer",
  // @ts-ignore
  transition: "background-color 150ms ease", // Matches card transition timing
}

// Hairline separator — same color as FleetOrderRow left section border
const $documentRowBorder: ViewStyle = {
  borderBottomWidth: 1,
  borderBottomColor: "rgba(17, 30, 44, 0.06)",
}

const $documentRowHover: ViewStyle = {
  backgroundColor: "rgba(17, 30, 44, 0.03)",
}

// PDF icon container — subtle background circle, visual anchor for each row
const $pdfIconContainer: ViewStyle = {
  width: 36,
  height: 36,
  borderRadius: 10,
  backgroundColor: "rgba(255, 56, 47, 0.06)", // Primary red tint at 6% opacity
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}

const $documentInfo: ViewStyle = {
  flex: 1,
  minWidth: 0, // Allows text truncation to work
}

const $documentName: TextStyle = {
  fontSize: 14,
  fontWeight: "500",
  color: colors.text, // #111E2C
  marginBottom: 3,
}

const $documentMeta: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 6,
}

// Small dot separator between date and size
const $metaDot: ViewStyle = {
  width: 3,
  height: 3,
  borderRadius: 1.5,
  backgroundColor: colors.palette.neutral400,
}

const $documentDate: TextStyle = {
  fontSize: 12,
  color: colors.textDim, // #666666
  fontWeight: "500",
}

const $documentSize: TextStyle = {
  fontSize: 12,
  color: colors.palette.neutral400, // #BDBDBD
}

// Download button — small dark pill matching header button style
const $downloadButton: ViewStyle = {
  width: 32,
  height: 32,
  borderRadius: 8,
  backgroundColor: colors.palette.neutral800, // #111E2C — matches header buttons
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  // @ts-ignore
  boxShadow: "0 1px 3px rgba(17, 30, 44, 0.12)", // Matches $headerButtonBase
  // @ts-ignore
  transition: "all 150ms ease",
}

// Compact pagination — arrows + page label in a single row
const $compactPagination: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: spacing.sm,
  paddingVertical: spacing.xs,
  borderTopWidth: 1,
  borderTopColor: "rgba(17, 30, 44, 0.06)",
}

const $pageArrow: ViewStyle = {
  width: 28,
  height: 28,
  borderRadius: 7,
  backgroundColor: colors.palette.neutral800,
  alignItems: "center",
  justifyContent: "center",
  // @ts-ignore
  cursor: "pointer",
  // @ts-ignore
  transition: "opacity 100ms ease",
}

const $pageArrowDisabled: ViewStyle = {
  opacity: 0.3,
  // @ts-ignore
  cursor: "default",
}

const $pageLabel: TextStyle = {
  fontSize: 13,
  fontWeight: "600",
  color: colors.textDim,
  minWidth: 40,
  textAlign: "center",
}

// Section header for Prüfberichte — matches dropdown header but lighter
const $sectionHeader: ViewStyle = {
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  paddingHorizontal: spacing.md,
  paddingVertical: 10,
  borderTopWidth: 1,
  borderTopColor: "rgba(17, 30, 44, 0.06)",
  borderBottomWidth: 1,
  borderBottomColor: "rgba(17, 30, 44, 0.06)",
  backgroundColor: "rgba(17, 30, 44, 0.02)",
}

const $sectionTitle: TextStyle = {
  fontSize: 14,
  fontWeight: "700",
  color: colors.text,
}

// Report icon — green tint to visually distinguish from red invoice icons
const $reportIconContainer: ViewStyle = {
  width: 36,
  height: 36,
  borderRadius: 10,
  backgroundColor: "rgba(76, 175, 80, 0.06)", // success green at 6% opacity
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}
